import { useEffect, useState } from 'react';
import axios from 'axios';

// Stale-while-revalidate client side. When the backend answers from its
// per-user snapshot (`_swr.refreshing === true`) it is already recomputing
// in the background; we re-GET the same URL a few seconds later and bump a
// per-path tick so every section that reads that endpoint re-fetches and
// swaps in the live numbers.
const REFETCH_DELAY_MS = 4000;
const MAX_ROUNDS = 3;
const scheduled = new Map();
const ticks = new Map();
const tickSubs = new Set();
const pendingSubs = new Set();
let pending = 0;

const pathOf = (url) => { try { return new URL(url, window.location.origin).pathname; } catch { return url; } };
const keyOf = (config) => `${pathOf(config.url)}?${JSON.stringify(config.params || {})}`;

function notifyPending(delta) {
  pending = Math.max(0, pending + delta);
  pendingSubs.forEach((fn) => fn(pending));
}

function bumpTick(path) {
  ticks.set(path, (ticks.get(path) || 0) + 1);
  tickSubs.forEach((fn) => fn(path));
}

function schedule(config, round) {
  const key = keyOf(config);
  if (scheduled.has(key)) return;
  notifyPending(1);
  const timer = setTimeout(async () => {
    scheduled.delete(key);
    try {
      const r = await axios.get(config.url, { params: config.params, withCredentials: config.withCredentials, headers: { 'x-swr-refetch': '1' } });
      const still = r?.data?._swr?.refreshing === true;
      if (!still || round + 1 >= MAX_ROUNDS) bumpTick(pathOf(config.url));
      if (still && round + 1 < MAX_ROUNDS) schedule(config, round + 1);
    } catch { /* ignore — next natural fetch will pick up fresh data */ }
    finally { notifyPending(-1); }
  }, REFETCH_DELAY_MS);
  scheduled.set(key, timer);
}

let installed = false;
export function installSwrRefresh() {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use((response) => {
    const cfg = response?.config || {};
    if (response?.data?._swr?.refreshing === true && (cfg.method || 'get').toLowerCase() === 'get' && !cfg.headers?.['x-swr-refetch']) {
      schedule(cfg, 0);
    }
    return response;
  });
}

// Returns a counter that increments when a background refresh of `pathSuffix`
// (e.g. '/portfolio/summary') has completed — add it to a fetch effect's deps.
export function useSwrTick(pathSuffix) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const fn = (path) => { if (path.endsWith(pathSuffix)) setTick((t) => t + 1); };
    tickSubs.add(fn);
    return () => tickSubs.delete(fn);
  }, [pathSuffix]);
  return tick;
}

export function useSwrPending() {
  const [n, setN] = useState(pending);
  useEffect(() => {
    pendingSubs.add(setN);
    return () => pendingSubs.delete(setN);
  }, []);
  return n;
}
